// Deactivating a doctor from the Agents page (actions/AgentsAction deleteDoctor)
// and signing a deactivated account out of every open tab (router.js).

/**
 * Timestamp of the latest accepting_chat_on among a doctor's activitiesV2 rows
 * (as RTDB returns them, oldest first); null when there is none.
 */
export const lastAcceptingStart = (rows) => {
  if (!rows) return null;
  const keys = Object.keys(rows);
  for (let i = keys.length - 1; i >= 0; i--) {
    if (rows[keys[i]] && rows[keys[i]].event === 'accepting_chat_on') return rows[keys[i]].timestamp;
  }
  return null;
};

/** When the doctor's current Accepting session began, from their last 10 activitiesV2 rows. */
export const acceptingSessionStart = async (database, doctorID) => {
  const snapshot = await database
    .ref('activitiesV2')
    .orderByChild('doctorID')
    .equalTo(doctorID)
    .limitToLast(10)
    .once('value');
  return lastAcceptingStart(snapshot.toJSON());
};

/**
 * How many chats a doctor really holds open: rows of /openconversations that
 * are theirs, active (status true) and not back in the queue. The same rule
 * PCA assignment v2 counts load by (pcaAssignment-v2/roster-v2 computeLoad).
 */
export const openChatsOf = (rows, doctorID) =>
  Object.values(rows || {}).filter(
    (chat) => chat && chat.doctorID === doctorID && chat.status === true && chat.queued !== true,
  ).length;

/**
 * The deactivate check reads the open chats themselves, not the
 * activeOnlineChats counter: closing a chat never lowers that counter, so it
 * blocked doctors with no chats and could let through one still holding
 * patients (QA, Nov 2026).
 */
export const countOpenChats = async (database, doctorID) => {
  const snapshot = await database
    .ref('openconversations')
    .orderByChild('doctorID')
    .equalTo(doctorID)
    .once('value');
  return openChatsOf(snapshot.val(), doctorID);
};

/**
 * The one multi-path update that deactivates a doctor. It also ends Accepting
 * (as PCD v2 does), so a reactivated account does not come back already taking
 * patients. If they were Accepting, it is recorded like the admin's own
 * Accepting switch, so Activity shows the session ending.
 */
export const deactivationUpdates = ({ doctorID, wasAccepting, now, key, sessionStart, admin, browserInfo, ipAddress }) => {
  const updates = {
    [`/doctors/${doctorID}/online`]: false,
    [`/doctors/${doctorID}/profileStatus`]: 'deleted',
    [`/onlineDoctors/${doctorID}`]: false,
    [`/doctors/${doctorID}/accepting_chat`]: false,
  };
  if (!wasAccepting) return updates;
  const at = now.getTime();
  const day = `${now.getUTCFullYear()}-${now.getUTCMonth() + 1}-${now.getUTCDate()}`;
  updates[`/doctors/${doctorID}/acceptingChatOffTime`] = at;
  updates[`/activities/${day}/${doctorID}/${key}`] = { acceptingChatOffTime: at, changedBy: admin };
  updates[`/activitiesV2/${key}`] = {
    doctorID,
    timestamp: at,
    event: 'accepting_chat_off',
    admin,
    browserInfo,
    ipAddress,
    // ydo-analytics' Doctors Activity chart reads startTimestamp on every
    // accepting_chat_off and fails without it. RTDB drops a null key, so with
    // no accepting_chat_on in recent activity the session is taken to start now.
    startTimestamp: sessionStart || at,
  };
  return updates;
};

/**
 * Follows the signed-in account's profileStatus and calls onDeactivated(user)
 * when it becomes "deleted". Call follow(user) on every auth change: it drops
 * the previous account's listener first. Clinic staff have no /doctors record,
 * so for them it never fires.
 */
export const deactivationWatcher = (database, onDeactivated) => {
  let current = null;
  return {
    follow(user) {
      if (current) {
        current.ref.off('value', current.listener);
        current = null;
      }
      if (!user) return;
      const ref = database.ref(`/doctors/${user.uid}/profileStatus`);
      const listener = ref.on('value', (snap) => {
        if (snap.val() === 'deleted') onDeactivated(user);
      }, () => {});
      current = { ref, listener };
    },
  };
};
